import type { Session } from "@supabase/supabase-js";
import { useLayoutEffect, useMemo, useRef } from "react";

import { takeConsolePrefetch } from "./console-bootstrap";
import { peekDashboardResource, useDashboardResource } from "./dashboard-resource";
import { readCachedProviderAccess, writeCachedProviderAccess } from "./provider-access-cache";
import {
  getProviderApplicationStatus,
  getProviderFulfillmentStanding,
  TinkrApiError,
  type ProviderApplicationStatusResponse,
  type ProviderFulfillmentStanding
} from "./tinkr-api";
import { getProviderShopStatus, type ProviderShopStatus } from "./tinkr-api/provider-shop-status";
import {
  deriveProviderDisplayState,
  type ProviderDisplayState,
  type ProviderDisplayStateInput
} from "./provider-display-state";

/**
 * Shared provider access status for the console shell (sidebar chip and
 * navigation) and AccessDashboard (lockouts, wizard, panels).
 *
 * Both used to need `/v1/provider/application`; only AccessDashboard read it,
 * so the shell guessed from `acceptingJobs`. One dashboard resource key now
 * serves both readers with a single request, joins the global refresh
 * control, and is dropped with the rest of the dashboard cache on sign-out
 * or account switch (App.tsx resetDashboardResourceCache).
 *
 * The localStorage hydration hint (provider-access-cache) keeps its narrow,
 * user-scoped, TTL-bound behaviour: it only fills the gap until the first
 * read settles and survives a transient revalidation failure (ONB-11).
 */
export function providerAccessResourceKey(userId: string): string {
  return `provider-access:${userId}`;
}

export type ProviderAccessStatus = {
  status: ProviderApplicationStatusResponse | null;
  /** `status` comes from a real API read (now or earlier in this session),
   * not only from the localStorage hydration hint, which carries no
   * eligibility. */
  confirmed: boolean;
  /** The latest read failed. With `status` present this is a stale-but-usable
   * snapshot; without it the console cannot tell what state the provider is in. */
  error: unknown;
  loading: boolean;
  refreshing: boolean;
  refresh: () => Promise<void>;
};

/**
 * The first access read starts from a layout effect, during the console's
 * first commit. The dashboard resource issues its own fetch from a microtask,
 * which on a slow phone only runs after the whole first passive-effect flush;
 * every panel waits on this read, so it leaves first. The resource's first
 * fetch adopts that read (still one request). The hand-over is per hook
 * instance and one-shot: later reads and explicit refreshes always ask the API
 * again, and a read older than a few seconds is never adopted.
 */
const PREFETCH_MAX_AGE_MS = 10_000;
type AccessPrefetch = { userId: string; at: number; read: Promise<ProviderApplicationStatusResponse> };

export function useProviderAccessStatus(session: Session | null): ProviderAccessStatus {
  const userId = session?.user?.id ?? null;
  const key = providerAccessResourceKey(userId ?? "anon");
  const hydrated = useMemo(() => readCachedProviderAccess(userId ?? undefined), [userId]);
  const prefetch = useRef<AccessPrefetch | null>(null);
  useLayoutEffect(() => {
    if (!session || !userId || peekDashboardResource(key) !== undefined) return;
    if (prefetch.current?.userId === userId) return;
    // The console bootstrap (P-01) starts this read as soon as a session is
    // known for the dashboard, before this shell has even loaded: adopt it
    // instead of asking twice.
    const read =
      takeConsolePrefetch<ProviderApplicationStatusResponse>("provider-application", userId) ??
      getProviderApplicationStatus(session);
    // The resource reader reports a failure; this copy only must not be unhandled.
    read.catch(() => undefined);
    prefetch.current = { userId, at: Date.now(), read };
  }, [session, userId, key]);
  const takePrefetch = () => {
    const entry = prefetch.current;
    if (!entry || entry.userId !== userId) return null;
    // Keep the user id so a StrictMode effect replay does not read again.
    prefetch.current = { ...entry, at: 0 };
    return Date.now() - entry.at < PREFETCH_MAX_AGE_MS ? entry.read : null;
  };
  // Approval, suspension and review decisions happen out-of-band. Until the
  // provider is active, re-read whenever the tab becomes visible (as before);
  // an active provider's status changes rarely, so it is re-read when older
  // than five minutes, on a global refresh, or after a provider write.
  const known = peekDashboardResource<ProviderApplicationStatusResponse>(key) ?? hydrated;
  const active = known?.provider?.operationalStatus === "active";
  const resource = useDashboardResource<ProviderApplicationStatusResponse>({
    key,
    enabled: Boolean(session),
    refreshOnFocus: !active,
    staleTimeMs: active ? 300_000 : 60_000,
    fetcher: async () => {
      const status = await (takePrefetch() ?? getProviderApplicationStatus(session!));
      writeCachedProviderAccess(userId ?? undefined, status);
      return status;
    }
  });
  const status = resource.data ?? hydrated ?? null;
  return {
    status,
    confirmed: resource.data !== undefined,
    error: resource.error ?? null,
    loading: Boolean(session) && status === null && !resource.error,
    refreshing: resource.refreshing,
    refresh: resource.refresh
  };
}

/** Narrow shop facts (name + intake switch). Shares its key with nothing
 * else; profile/availability writes invalidate it (client.ts). */
export function useProviderShopStatus(session: Session | null, enabled: boolean) {
  return useDashboardResource<ProviderShopStatus | null>({
    key: `warm-shop-status:provider:${session?.user?.id ?? "anon"}`,
    enabled: enabled && session !== null,
    fetcher: async (signal) => {
      try {
        return (await getProviderShopStatus(session!, signal)).provider;
      } catch (error) {
        // No provider record yet (404) or no provider access (401/403): no
        // shop facts, not an error. The access status explains the state.
        // Same semantics as providerShopStatusFetcher, which the console
        // bootstrap uses to fill this key early (P-01).
        if (error instanceof TinkrApiError && (error.status === 404 || error.status === 403 || error.status === 401)) {
          return null;
        }
        throw error;
      }
    }
  });
}

/** Strike standing. Same key as the Payouts panel, so the read is shared. */
export function useProviderFulfillmentStanding(session: Session | null, enabled: boolean) {
  return useDashboardResource<ProviderFulfillmentStanding>({
    key: `provider-fulfillment-standing:${session?.user?.id ?? "anon"}`,
    enabled: enabled && session !== null,
    staleTimeMs: 300_000,
    fetcher: async () => (await getProviderFulfillmentStanding(session!)).standing
  });
}

const SIGNED_OUT_ACCESS: ProviderApplicationStatusResponse = {
  ok: true,
  network: { providerNetworkLive: false, updatedAt: null },
  application: null,
  provider: null,
  gate: { status: "not_submitted", canStartProviderOnboarding: false }
};

export type ProviderConsoleState = {
  access: ProviderAccessStatus;
  display: ProviderDisplayState;
  shop: ProviderShopStatus | null;
  standing: ProviderFulfillmentStanding | null;
  /** Re-reads what "open for jobs" depends on (the strike standing, and the
   * access summary while only a hydration hint is known). */
  refreshOfferStatus: () => Promise<void>;
};

/**
 * The derived console state. Shop status and strike standing are only read
 * once the provider record is known to be active: a setting-up or locked
 * account has no live intake to describe.
 */
export function useProviderConsoleState(session: Session | null): ProviderConsoleState {
  const access = useProviderAccessStatus(session);
  const active = access.status?.provider?.operationalStatus === "active";
  const shop = useProviderShopStatus(session, active);
  const standing = useProviderFulfillmentStanding(session, active);
  // "Open for jobs" is claimed only once the reads that could contradict it
  // have settled (E-E-2): a hydrated hint has no eligibility, and the strike
  // pause is not part of the eligibility decision.
  const offerFacts: ProviderDisplayStateInput["offerFacts"] = !access.confirmed
    ? access.error ? "failed" : "loading"
    : standing.data !== undefined ? "ready" : standing.error ? "failed" : "loading";
  // Signed-out renders (isolated panel tests) have nothing to wait for: they
  // see the pre-application setup surface, as before.
  const display = !session ? deriveProviderDisplayState({ access: SIGNED_OUT_ACCESS }) : deriveProviderDisplayState({
    access: access.status,
    accessFailed: Boolean(access.error) && access.status === null,
    acceptingJobs: shop.data ? shop.data.acceptingJobs : null,
    standing: standing.data ?? null,
    offerFacts
  });
  const refreshOffers = access.confirmed ? undefined : access.refresh;
  return {
    access,
    display,
    shop: shop.data ?? null,
    standing: standing.data ?? null,
    refreshOfferStatus: async () => {
      await Promise.all([refreshOffers?.(), standing.refresh()]);
    }
  };
}
